import {FC, useState} from "react"
import Image from "next/image"
import {useRouter} from "next/router"

import {UserHeaderWithBackButton} from "components/UserHeader"
import {CurriculumStatus} from "datasources/prisma"
import {Button} from "components/button"
import {useModal} from "lib/hooks"

import clockIconSrc from "public/icons/clock.png"

import {OngoingCourseModal} from "components/modal"

import {Footer} from "components/Layout/Footer"
import {CourseStates} from "datasources/scheduler"
import {trpc} from "datasources/trpc"
import Loader from "components/Loader"
import styles from "./course.module.css"
import {GlossaryButton} from "../Glossary/GlossaryButton"
import {Block} from "./Block"
import {BuildUpDescription} from "./BuildUpDescription"
import {CheckUpDescription} from "./CheckUpDescription"
import {EyeOfTheStormDescription} from "./EyeOfTheStormDescription"
import {BurnoutDescription} from "./BurnoutDescription"
import {CoursePageProps} from "./CoursePageProps"
import {GuiltAndRegret} from "./GuiltAndRegret"
import {OvercomingFearAndAnxiety} from "./OvercomingFearAndAnxiety"
import {Depression} from "./Depression"
import {BurnoutRecovery} from "./BurnoutRecovery"
import {InternalCritics} from "./InternalCritics"
import {PleaserNoMore} from "./PleaserNoMore"

export interface ButtonState {
  title: string
  url: string
  forbidden?: boolean
}

interface HeaderProps {
  title: string
  isAuthenticated: boolean
  duration: string
  color?: string | null
  image: {
    url?: string
    width?: number
    height?: number
  }
}

const Header: FC<HeaderProps> = ({title, duration, isAuthenticated, color, image}) => (
  <header
    className="w-full pb-24 tablet:pb-32 bg-celadon dots-bg"
    style={{backgroundColor: color ?? undefined}}
  >
    <Block>
      <UserHeaderWithBackButton
        {...{isAuthenticated}}
        href="/courses"
        className="pb-0 px-0 mb-14"
      />
    </Block>

    <Block className="flex flex-col tablet:flex-row-reverse tablet:items-center tablet:justify-between">
      <div className="relative w-60 self-center shrink-0 tablet:w-80">
        <Image
          src={image.url as string}
          width={image.width as number}
          height={image.height as number}
          layout="responsive"
          alt=""
        />
      </div>
      <div className="mt-5 tablet:mt-0">
        <h1 className="m-0 tablet:text-display">{title}</h1>
        <div className="mt-6 flex items-center">
          <div className="w-8">
            <Image src={clockIconSrc} layout="responsive" alt="" priority />
          </div>
          <h6 className="ml-4 mt-2">{duration}</h6>
        </div>
      </div>
    </Block>
  </header>
)

const OnGoungCourseModalButton: FC<ButtonState> = ({title}) => {
  const [modal, showModal] = useModal(({isOpened, close}) => (
    <OngoingCourseModal {...{isOpened, close}}>
      <div className="pt-4 flex flex-col items-center">
        <p className="mt-0">
          You are already taking another course, complete that one first, then this one
          will open.
        </p>
        <p>If you no longer want to take the current course, write to support.</p>
      </div>
    </OngoingCourseModal>
  ))

  return (
    <>
      <Button onClick={showModal}>{title}</Button>

      {modal}
    </>
  )
}

const assessmentSlugs = ["eye-of-the-storm", "check-up"]

const useButtonState = (
  courseId: string,
  courseState: CourseStates | null,
  isAuthenticated: boolean,
  curriculumCourse: string | null,
  curruculumStatus: CurriculumStatus | null,
  userId: string | null,
  slug?: string | null
) => {
  const courseApply = trpc.course.apply.useMutation()
  // TODO (alexander): analytics
  // const {track} = useAuthozrizedAnalytics()

  if (!isAuthenticated || !userId) {
    return {title: "Create account", url: "/register"}
  }

  if (!courseState || courseState === "NOT_STARTED") {
    if (!curriculumCourse && !!slug && assessmentSlugs.includes(slug)) {
      // Нет никаких курсов
      return {title: "Start My Journey!", url: `/assessment/${courseId}`}
    }

    if (curriculumCourse && curruculumStatus
      && ["ONGOING", "READY_TO_FINISH"].includes(curruculumStatus)) {
      // Есть другой активный курс
      return {
        title: "You already have one ongoing course",
        forbidden: true,
        url: `/authorized/${userId}/courses`
      }
    }

    return {title: "Start My Journey!",
      onClick: async () => {
        const response = await courseApply.mutateAsync({courseId})
        if (response.success) {
          // track("course_apply", {courseId})
          window.location.href = "/dashboard"
          // router.push(`/authorized/${userId}/dashboard`,
          // undefined, {unstable_skipClientCache: true})
        }
      }}
  }

  if (courseState && ["ONGOING", "READY_TO_FINISH"].includes(courseState)) {
    return {title: "Go to my course", url: `/authorized/${userId}/dashboard`}
  }

  if (courseState && ["COMPLETED", "COMPLETED_FAIL"].includes(courseState)) {
    return {title: "This course is complete", url: `/authorized/${userId}/courses`}
  }

  return {title: "Sorry, this course is unavailable", url: "/"}
}

export const CoursePage: FC<CoursePageProps> = ({
  course,
  courseState,
  isAuthenticated,
  curriculumCourse,
  curruculumStatus,
  userId
}) => {
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const button = useButtonState(
    course.id || "",
    courseState,
    isAuthenticated,
    curriculumCourse,
    curruculumStatus,
    userId,
    course.slug
  )

  const onButtonClick = async () => {
    try {
      if (button.url) {
        router.push(button.url)
      } else if (button.onClick) {
        setLoading(true)
        await button.onClick()
      }
    } finally {
      setLoading(false)
    }
  }

  const title = course.name as string
  const duration = course.displayDuration as string

  let description
  switch (course.slug) {
    case "burnout-recovery":
      description = <BurnoutDescription course={course} />
      break
    case "eye-of-the-storm":
      description = <EyeOfTheStormDescription course={course} />
      break
    case "check-up":
      description = <CheckUpDescription course={course} />
      break
    case "build-up":
      description = <BuildUpDescription course={course} />
      break
    case "guilt-and-regret":
      description = <GuiltAndRegret course={course} />
      break
    case "fear-and-anxiety":
      description = <OvercomingFearAndAnxiety course={course} />
      break
    case "depression":
      description = <Depression course={course} />
      break
    case "burnout":
      description = <BurnoutRecovery course={course} />
      break
    case "internal-critics":
      description = <InternalCritics course={course} />
      break
    case "pleaser-no-more":
      description = <PleaserNoMore course={course} />
      break
    default:
      description = null
  }

  if (loading) {
    return <Loader />
  }

  return (
    <div className="w-full h-full">
      <Header
        {...{
          title,
          duration,
          isAuthenticated,
          color: course.color?.hex,
          image: course.image!
        }}
      />
      {description}
      {course.footerLinks.length > 0 && (
        <Block className="mt-12 flex flex-col tablet:flex-row items-start space-y-2 tablet:space-y-0 tablet:space-x-4">
          {course.footerLinks.map(link => (
            <GlossaryButton
              key={link.id}
              title={link.title || ""}
              description={link.description[0].content?.value}
              buttonStyle="link"
            />
          ))}
        </Block>
      )}
      <Footer className="mt-32">
        <div className={styles.start}>
          {button.forbidden ? (
            <OnGoungCourseModalButton {...button} />
          ) : (
            <Button onClick={onButtonClick}>{button.title}</Button>
          )}
        </div>
      </Footer>
    </div>
  )
}
